import React, { useState, useEffect, useRef } from 'react';
import tess from '../../tess.png';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Badge,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Menu,
  MenuItem,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Send as SendIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { chatAPI, ChatMessage, ChatSession } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import SearchResultsPanel from '../Search/SearchResultsPanel';
import AllCasesPanel from './AllCasesPanel';
import MessageFormatter from './MessageFormatter';
import ReviewComponent from './ReviewComponent';
import InlineQuestionOptions from './InlineQuestionOptions';
import BaseQuestionsForm from './BaseQuestionsForm';

interface ChatInterfaceProps {
  onSearchResults?: (results: any[]) => void;
  sessionToLoad?: string | null;
  onSessionLoaded?: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ onSearchResults, sessionToLoad, onSessionLoaded }) => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentSession, setCurrentSession] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showSessions, setShowSessions] = useState(false);
  const [currentSearchResults, setCurrentSearchResults] = useState<any[]>([]);
  const [allCases, setAllCases] = useState<any[]>([]);
  const [chatPhase, setChatPhase] = useState<string>('');
  const [completionPercentage, setCompletionPercentage] = useState<number>(0);
  const [sessionEnded, setSessionEnded] = useState<boolean>(false);
  const [isSubmittingOptions, setIsSubmittingOptions] = useState<boolean>(false);
  const [typingMessage, setTypingMessage] = useState<string>('');
  const [showTypingIndicator, setShowTypingIndicator] = useState<boolean>(false);
  const [hasActiveOptions, setHasActiveOptions] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [customOptionText, setCustomOptionText] = useState<string>('');
  const [canSubmitOptions, setCanSubmitOptions] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Sharing states
  const [shareMenuAnchor, setShareMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedSessionForShare, setSelectedSessionForShare] = useState<string | null>(null);
  const [showShareSnackbar, setShowShareSnackbar] = useState(false);
  const [shareSnackbarMessage, setShareSnackbarMessage] = useState('');

  // Review states
  const [selectedTab, setSelectedTab] = useState(0); // 0 = Chat, 1 = Reviews

  // Base questions form states
  const [showBaseQuestionsForm, setShowBaseQuestionsForm] = useState(false);
  const [baseQuestionsGreeting, setBaseQuestionsGreeting] = useState('');

  useEffect(() => {
    loadSessions();
    
    // Auto-show form if starting fresh (no messages, no session)
    const initializeForm = async () => {
      if (messages.length === 0 && !currentSession) {
        try {
          const data = await chatAPI.getBaseQuestions();
          setBaseQuestionsGreeting(data.greeting);
          setShowBaseQuestionsForm(true);
        } catch (err) {
          console.error('Error getting base questions:', err);
        }
      }
    };
    
    initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle sessionToLoad prop
  useEffect(() => {
    if (sessionToLoad && sessionToLoad !== currentSession) {
      loadSessionHistory(sessionToLoad);
      setCurrentSession(sessionToLoad);
      setSelectedTab(1); // Switch to Notes tab
      if (onSessionLoaded) {
        onSessionLoaded();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToLoad]);

  useEffect(() => {
    scrollToBottom();
    
    // Check if the last message has active options
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      const hasOptions = !lastMessage.isOptimistic && 
                        lastMessage.follow_up_questions && 
                        lastMessage.follow_up_questions.length > 0 && 
                        lastMessage.follow_up_questions[0].options && 
                        !showTypingIndicator && 
                        !isSubmittingOptions;
      setHasActiveOptions(!!hasOptions);
    } else {
      setHasActiveOptions(false);
    }
  }, [messages, showTypingIndicator, isSubmittingOptions]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSessions = async () => {
    try {
      const response = await chatAPI.getSessions();
      setSessions(response.sessions);
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  const loadSessionHistory = async (sessionId: string) => {
    setShowTypingIndicator(true);
    setTypingMessage('Loading chat history...');
    setError(null);
    
    try {
      const history = await chatAPI.getSessionHistory(sessionId);
      const formattedMessages: ChatMessage[] = [];
      
      // Load session context and status from history
      const sessionContext = history.session_context || {};
      
      history.messages.forEach((msg: any) => {
        formattedMessages.push({
          session_id: sessionId,
          session_created: false,
          user_input: msg.user_input,
          ai_response: msg.ai_response,
          extracted_info: msg.extracted_info,
          search_results: msg.search_results || [],
          session_context: sessionContext,
          processing_time_ms: msg.processing_time_ms || 0,
        });
      });
      
      setMessages(formattedMessages);
      setCurrentSession(sessionId);
      
      // Restore chat state from the last message
      if (history.messages.length > 0) {
        const lastMessage = history.messages[history.messages.length - 1];
        setChatPhase(lastMessage.phase || 'base_questions');
        setCompletionPercentage(lastMessage.completion_percentage || 0);
        setSessionEnded(lastMessage.ready_for_diagnosis || false);
        
        // Update search results if available
        const lastSearchResults = lastMessage.search_results || [];
        setCurrentSearchResults(lastSearchResults);
        if (onSearchResults && lastSearchResults.length > 0) {
          onSearchResults(lastSearchResults);
        }
      } else {
        // Reset chat state for empty session
        setChatPhase('base_questions');
        setCompletionPercentage(0);
        setSessionEnded(false);
        setCurrentSearchResults([]);
      }
      
      console.log(`✅ Loaded session ${sessionId} with ${formattedMessages.length} messages`);
      console.log('Session context:', sessionContext);
    } catch (err) {
      console.error('Error loading session history:', err);
      setError('Failed to load chat history');
    } finally {
      setShowTypingIndicator(false);
    }
  };

  const handleOptionSubmit = async (selectedOptions: string[], customText?: string): Promise<void> => {
    let userMessage: string;
    
    if (selectedOptions.length === 1) {
      userMessage = selectedOptions[0];
    } else {
      userMessage = selectedOptions.join(', ');
    }
    
    // If there's custom text for "Other", append it
    if (customText) {
      userMessage = customText;
    }
    
    // Show optimistic user message immediately
    const optimisticUserMessage = {
      session_id: currentSession || '',
      session_created: false,
      user_input: userMessage,
      ai_response: '',
      extracted_info: {},
      search_results: [],
      session_context: {},
      processing_time_ms: 0,
      isOptimistic: true,
    };
    
    setMessages(prev => [...prev, optimisticUserMessage]);
    setIsSubmittingOptions(true);
    setShowTypingIndicator(true);
    setTypingMessage('TESS is analyzing your response...');
    setError(null);

    try {
      console.log('Sending option selection:', userMessage, 'Session:', currentSession);
      const response = await chatAPI.sendMessage(userMessage, currentSession || undefined);
      console.log('Received orchestrated response:', response);
      console.log('Phase:', response.phase, 'Completion:', response.completion_percentage + '%');
      
      const searchResults = response.search_results || [];
      const allCasesData = response.all_cases || [];
      
      const newMessage: ChatMessage = {
        session_id: response.session_id,
        session_created: response.session_created,
        user_input: userMessage,
        ai_response: response.message,
        extracted_info: response.extracted_info,
        search_results: searchResults,
        session_context: response.extracted_info,
        processing_time_ms: response.processing_time_ms,
        message_type: response.message_type,
        phase: response.phase,
        completion_percentage: response.completion_percentage,
        ready_for_diagnosis: response.ready_for_diagnosis,
        follow_up_questions: response.follow_up_questions,
      };

      console.log('Adding new message to state:', newMessage);
      // Replace the optimistic message with the complete conversation turn
      setMessages(prev => {
        const withoutOptimistic = prev.filter(msg => !msg.isOptimistic);
        return [...withoutOptimistic, newMessage];
      });
      setCurrentSession(response.session_id);
      
      // Update orchestrated chat state
      setChatPhase(response.phase);
      setCompletionPercentage(response.completion_percentage);
      setSessionEnded(response.session_ended || false);

      // Update search results and all cases if callback provided
      if (onSearchResults && searchResults.length > 0) {
        onSearchResults(searchResults);
      }
      setCurrentSearchResults(searchResults);
      setAllCases(allCasesData);

      // Reload sessions if a new one was created
      if (response.session_created) {
        loadSessions();
      }
    } catch (err: any) {
      console.error('Error sending option selection:', err);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => !msg.isOptimistic));
      setError(err.response?.data?.error || 'Failed to send message');
    } finally {
      setShowTypingIndicator(false);
      setIsSubmittingOptions(false);
    }
  };

  const sendMessage = async () => {
    // Handle option submission if options are active
    if (hasActiveOptions && canSubmitOptions) {
      await handleOptionSubmit(selectedOptions, customOptionText);
      // Clear option state
      setSelectedOptions([]);
      setCustomOptionText('');
      setCanSubmitOptions(false);
      return;
    }

    // Handle regular message
    if (!message.trim()) return;

    const userMessage = message;
    setMessage('');
    setError(null);

    // Show optimistic user message immediately
    const optimisticUserMessage: ChatMessage = {
      session_id: currentSession || '',
      session_created: false,
      user_input: userMessage,
      ai_response: '',
      extracted_info: {},
      search_results: [],
      session_context: {},
      processing_time_ms: 0,
      isOptimistic: true,
    };
    
    setMessages(prev => [...prev, optimisticUserMessage]);
    setShowTypingIndicator(true);
    setTypingMessage('TESS is thinking...');

    try {
      console.log('Sending message:', userMessage, 'Session:', currentSession);
      const response = await chatAPI.sendMessage(userMessage, currentSession || undefined);
      console.log('Received orchestrated response:', response);
      console.log('Phase:', response.phase, 'Completion:', response.completion_percentage + '%');
      
      const searchResults = response.search_results || [];
      const allCasesData = response.all_cases || [];
      
      const newMessage: ChatMessage = {
        session_id: response.session_id,
        session_created: response.session_created,
        user_input: userMessage,
        ai_response: response.message,  // orchestrated chat returns 'message' instead of 'ai_response'
        extracted_info: response.extracted_info,
        search_results: searchResults,
        session_context: response.extracted_info, // use extracted_info as session context
        processing_time_ms: response.processing_time_ms,
        message_type: response.message_type,
        phase: response.phase,
        completion_percentage: response.completion_percentage,
        ready_for_diagnosis: response.ready_for_diagnosis,
        follow_up_questions: response.follow_up_questions,
      };

      console.log('Adding new message to state:', newMessage);
      // Replace the optimistic message with the complete conversation turn
      setMessages(prev => {
        const withoutOptimistic = prev.filter(msg => !msg.isOptimistic);
        return [...withoutOptimistic, newMessage];
      });
      setCurrentSession(response.session_id);
      
      // Update orchestrated chat state
      setChatPhase(response.phase);
      setCompletionPercentage(response.completion_percentage);
      setSessionEnded(response.session_ended || false);

      // Update search results and all cases if callback provided
      if (onSearchResults && searchResults.length > 0) {
        onSearchResults(searchResults);
      }
      setCurrentSearchResults(searchResults);
      setAllCases(allCasesData);

      // Reload sessions if a new one was created
      if (response.session_created) {
        loadSessions();
      }
    } catch (err: any) {
      console.error('Error sending message:', err);
      console.error('Error response:', err.response);
      console.error('Error details:', err.response?.data);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => !msg.isOptimistic));
      setError(err.response?.data?.detail || err.message || 'Failed to send message');
    } finally {
      setShowTypingIndicator(false);
    }
  };

  const startNewSession = async () => {
    setMessages([]);
    setCurrentSession(null);
    setCurrentSearchResults([]);
    setError(null);
    setChatPhase('base_questions');
    setCompletionPercentage(0);
    setSessionEnded(false);
    setShowSessions(false);
    
    // Clear search results in parent component
    if (onSearchResults) {
      onSearchResults([]);
    }
    
    // Show base questions form
    try {
      const data = await chatAPI.getBaseQuestions();
      setBaseQuestionsGreeting(data.greeting);
      setShowBaseQuestionsForm(true);
    } catch (err) {
      console.error('Error getting base questions:', err);
      // Fall back to regular chat if form fetch fails
      setShowBaseQuestionsForm(false);
    }
    
    console.log('🆕 Started new chat session');
  };

  const handleBaseQuestionsSubmit = async (answers: Record<string, any>) => {
    setShowTypingIndicator(true);
    setTypingMessage('Processing your information...');

    try {
      console.log('Submitting base questions form:', answers);
      const response = await chatAPI.submitBaseQuestions(answers, currentSession || undefined);
      console.log('Base questions response:', response);

      const searchResults = response.search_results || [];
      const allCasesData = response.all_cases || [];

      const newMessage: ChatMessage = {
        session_id: response.session_id,
        session_created: response.session_created,
        user_input: 'Base questions form submitted',
        ai_response: response.message,
        extracted_info: response.extracted_info,
        search_results: searchResults,
        session_context: response.extracted_info,
        processing_time_ms: response.processing_time_ms,
        message_type: response.message_type,
        phase: response.phase,
        completion_percentage: response.completion_percentage,
        ready_for_diagnosis: response.ready_for_diagnosis,
        follow_up_questions: response.follow_up_questions,
      };

      setMessages([newMessage]);
      setCurrentSession(response.session_id);
      setChatPhase(response.phase);
      setCompletionPercentage(response.completion_percentage);
      setSessionEnded(response.session_ended || false);
      
      // Hide the form
      setShowBaseQuestionsForm(false);

      // Update search results and all cases if callback provided
      if (onSearchResults && searchResults.length > 0) {
        onSearchResults(searchResults);
      }
      setCurrentSearchResults(searchResults);
      setAllCases(allCasesData);

      // Reload sessions
      await loadSessions();
      
      console.log('✅ Base questions form submitted successfully');
    } catch (err: any) {
      console.error('Error submitting base questions:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to submit form');
      throw err;
    } finally {
      setShowTypingIndicator(false);
    }
  };

  const deleteSession = async (sessionId: string) => {
    try {
      await chatAPI.deleteSession(sessionId);
      await loadSessions();
      if (currentSession === sessionId) {
        startNewSession();
      }
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  const handleShareClick = (event: React.MouseEvent<HTMLElement>, sessionId: string) => {
    event.stopPropagation();
    setShareMenuAnchor(event.currentTarget);
    setSelectedSessionForShare(sessionId);
  };

  const handleShareMenuClose = () => {
    setShareMenuAnchor(null);
    setSelectedSessionForShare(null);
  };

  const shareSession = async () => {
    if (!selectedSessionForShare) return;

    try {
      const result = await chatAPI.shareSession(selectedSessionForShare);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(result.share_url);
      setShareSnackbarMessage('Share link copied to clipboard!');
      setShowShareSnackbar(true);
      
      console.log('✅ Session shared successfully:', result.share_url);
    } catch (err) {
      console.error('Error sharing session:', err);
      setShareSnackbarMessage('Failed to share session');
      setShowShareSnackbar(true);
    } finally {
      handleShareMenuClose();
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getFieldEmoji = (field: string): string => {
    const emojiMap: { [key: string]: string } = {
      'pet': '🐕',
      'age': '📅',
      'gender': '⚧️',
      'male_reproductive': '♂️',
      'female_reproductive': '♀️',
      'symptoms': '🤒',
      'possible_symptoms': '🤔',
      'condition': '🏥',
      'history': '📋',
      'initial_symptoms': '🩺',
      'symptom_duration': '⏰',
      'diagnosis': '🔬',
      'priority': '🚨',
      'negated_pet': '🚫🐕',
      'negated_age': '🚫📅',
      'negated_gender': '🚫⚧️',
      'negated_male_reproductive': '🚫♂️',
      'negated_female_reproductive': '🚫♀️',
      'negated_symptoms': '🚫🤒',
      'negated_possible_symptoms': '🚫🤔',
      'negated_condition': '🚫🏥',
      'negated_history': '🚫📋',
      'negated_diagnosis': '🚫🔬',
    };
    return emojiMap[field] || '🔍';
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        background: '#22537f',
        color: 'white',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box 
            component="img"
            src={tess}
            alt="Tess"
            sx={{ 
              width: '48px',
              height: '48px',
              borderRadius: '50%'
            }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              TESS
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
              Your Vet Direction Chat
            </Typography>
            {chatPhase && (
              <Typography variant="body2" sx={{ color: 'white', opacity: 0.9, fontSize: '0.75rem' }}>
                Phase: {chatPhase.replace('_', ' ')} • {completionPercentage.toFixed(0)}% complete
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Copy Session Link Button - Only show when there's an active session */}
          {currentSession && (
            <IconButton
              color="inherit"
              onClick={async () => {
                try {
                  const result = await chatAPI.shareSession(currentSession);
                  await navigator.clipboard.writeText(result.share_url);
                  setShareSnackbarMessage('Session link copied to clipboard!');
                  setShowShareSnackbar(true);
                } catch (err) {
                  console.error('Error copying session link:', err);
                  setShareSnackbarMessage('Failed to copy session link');
                  setShowShareSnackbar(true);
                }
              }}
              title="Copy session link"
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                }
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          )}
          <IconButton
            color="inherit"
            onClick={() => setShowSessions(true)}
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              }
            }}
          >
            <Badge badgeContent={sessions.length} color="secondary">
              <HistoryIcon />
            </Badge>
          </IconButton>
          <Button
            variant="contained"
            onClick={startNewSession}
            startIcon={<AddIcon />}
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            New Chat
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      {currentSession && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab label="Chat" />
            <Tab label="Notes" />
          </Tabs>
        </Box>
      )}

      {/* Content Area */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {selectedTab === 0 ? (
          // Chat Tab Content
          <>
            {/* Chat Messages */}
            <Box sx={{ 
              flex: 1, 
              overflowY: 'auto', 
              p: 3,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(0, 0, 0, 0.2)',
              },
            }}>
        {messages.length === 0 ? (
          showBaseQuestionsForm ? (
            <Box sx={{ p: 3 }}>
              <BaseQuestionsForm 
                onSubmit={handleBaseQuestionsSubmit}
                greeting={baseQuestionsGreeting}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, maxWidth: 480, mx: 'auto' }}>
                <Box
                  component="img"
                  src={tess}
                  alt="Tess"
                  sx={{ 
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                  }}
                />
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'white',
                    border: '1px solid black',
                    borderRadius: 2,
                    borderTopLeftRadius: 0,
                    fontSize: '0.9375rem',
                    lineHeight: 1.5,
                    color: '#1A1A1A',
                    width: '100%',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: -8,
                      width: 8,
                      height: 8,
                      borderTop: '1px solid black',
                      borderLeft: '1px solid black',
                      backgroundColor: 'white',
                      transform: 'translateY(8px) rotate(-45deg)',
                    }
                  }}
                >
                  Hello! My name is TESS. I'm your veterinary triage assistant. Please describe your pet's symptoms and I'll help assess the situation.
                  <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.7 }}>
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )
        ) : (
          messages.map((msg, index) => (
            <Box key={index} sx={{ mb: 4 }}>
              {/* User Message */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', mb: 2, gap: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    maxWidth: '70%',
                    background: '#7B5D94',
                    color: 'white',
                    borderRadius: 2,
                    borderBottomRightRadius: 0,
                    fontSize: '0.9375rem',
                    lineHeight: 1.5,
                  }}
                >
                  {msg.user_input}
                  <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.7 }}>
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
                <Avatar 
                  sx={{ 
                    width: 32,
                    height: 32,
                    bgcolor: '#22537f',
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }}
                >
                  {user?.first_name?.[0] || user?.username?.[0] || '?'}
                </Avatar>
              </Box>

              {/* AI Response - Only show if not optimistic and has AI response */}
              {!msg.isOptimistic && msg.ai_response && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, maxWidth: '85%' }}>
                  <Box
                    component="img"
                    src={tess}
                    alt="Tess"
                    sx={{ 
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                    }}
                  />
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'white',
                      border: '1px solid black',
                      borderRadius: 2,
                      borderTopLeftRadius: 0,
                      fontSize: '0.9375rem',
                      lineHeight: 1.5,
                      color: '#1A1A1A',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: -8,
                        width: 8,
                        height: 8,
                        borderTop: '1px solid black',
                        borderLeft: '1px solid black',
                        backgroundColor: 'white',
                        transform: 'translateY(8px) rotate(-45deg)',
                      }
                    }}
                  >
                    {/* Use formatted display for diagnosis messages and other structured content */}
                    {msg.ai_response && (
                      msg.ai_response.includes('🎯 **') || 
                      msg.ai_response.includes('**🔬') || 
                      msg.ai_response.includes('**🚨') ||
                      msg.ai_response.includes('**🔴') ||
                      msg.ai_response.includes('**🟠') ||
                      msg.ai_response.includes('**🟡') ||
                      msg.ai_response.includes('**🔍') ||
                      msg.ai_response.includes('**📋') ||
                      msg.ai_response.includes('**🏥') ||
                      msg.ai_response.includes('💡 *') ||
                      msg.ai_response.includes('Likely Diagnosis') ||
                      msg.ai_response.includes('Priority Level') ||
                      msg.ai_response.includes('Matching Symptoms') ||
                      msg.ai_response.includes('Recommendation') ||
                      msg.ai_response.includes('Next Steps')
                    ) ? (
                      <MessageFormatter message={msg.ai_response} />
                    ) : (
                      msg.ai_response
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.7 }}>
                      {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>

                  {/* Extracted Information Display */}
                  {msg.extracted_info && Object.keys(msg.extracted_info).length > 0 && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: 2,
                        fontSize: '0.875rem',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#22537f', fontWeight: 600 }}>
                        📊 Extracted Information:
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Object.entries(msg.extracted_info).map(([key, value]) => {
                          if (!value || (Array.isArray(value) && value.length === 0)) return null;
                          
                          // Hide internal system fields
                          const hiddenFields = ['base_questions_complete', 'structured_symptoms_asked', 'initial_symptoms'];
                          if (hiddenFields.includes(key)) return null;
                          
                          const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                          const emoji = getFieldEmoji(key);
                          
                          return (
                            <Box
                              key={key}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1.5,
                                py: 0.5,
                                bgcolor: key.startsWith('negated_') ? '#ffe6e6' : '#e8f4fd',
                                border: key.startsWith('negated_') ? '1px solid #ffcccc' : '1px solid #b3d9ff',
                                borderRadius: 1,
                                fontSize: '0.8125rem',
                              }}
                            >
                              <span>{emoji}</span>
                              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                {key.replace('_', ' ').replace('negated ', '❌ ')}:
                              </Typography>
                              <Typography variant="caption">
                                {displayValue}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Diagnosis Information Display */}
                  {msg.followup_analysis?.case_status === 'similarity_match' && msg.followup_analysis.diagnosed_condition && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: '#e8f5e8',
                        border: '2px solid #4caf50',
                        borderRadius: 2,
                        fontSize: '0.875rem',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#2e7d32', fontWeight: 700 }}>
                        🎯 DIAGNOSIS FOUND ({(msg.followup_analysis.similarity_score! * 100).toFixed(0)}% Match):
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                            🔬 Diagnosis:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#1b5e20', fontWeight: 500 }}>
                            {msg.followup_analysis.diagnosed_condition}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                            🚨 Priority:
                          </Typography>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor: 
                                msg.followup_analysis.recommended_priority === 'emergency' ? '#ffebee' :
                                msg.followup_analysis.recommended_priority === 'urgent' ? '#fff3e0' :
                                msg.followup_analysis.recommended_priority === 'sick' ? '#e8f5e8' : '#f5f5f5',
                              border: 
                                msg.followup_analysis.recommended_priority === 'emergency' ? '1px solid #f44336' :
                                msg.followup_analysis.recommended_priority === 'urgent' ? '1px solid #ff9800' :
                                msg.followup_analysis.recommended_priority === 'sick' ? '1px solid #4caf50' : '1px solid #9e9e9e',
                            }}
                          >
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontWeight: 600, 
                                textTransform: 'uppercase',
                                color: 
                                  msg.followup_analysis.recommended_priority === 'emergency' ? '#d32f2f' :
                                  msg.followup_analysis.recommended_priority === 'urgent' ? '#f57c00' :
                                  msg.followup_analysis.recommended_priority === 'sick' ? '#388e3c' : '#616161'
                              }}
                            >
                              {msg.followup_analysis.recommended_priority}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {msg.followup_analysis.urgency_assessment && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                              💡 {msg.followup_analysis.urgency_assessment}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                </Box>
              </Box>
              )}

              {/* Inline Question Options - Display below the entire AI message */}
              {index === messages.length - 1 && 
               !msg.isOptimistic &&
               msg.follow_up_questions && 
               msg.follow_up_questions.length > 0 && 
               msg.follow_up_questions[0].options && 
               !showTypingIndicator && !isSubmittingOptions && (
                <Box sx={{ mt: 2, ml: 6 }}>
                  {msg.follow_up_questions.map((question, qIndex) => (
                    <InlineQuestionOptions
                      key={qIndex}
                      options={question.options || []}
                      multipleSelection={question.multiple_selection || false}
                      onSubmit={handleOptionSubmit}
                      onSelectionChange={(options, customText, canSubmit) => {
                        setSelectedOptions(options);
                        setCustomOptionText(customText || '');
                        setCanSubmitOptions(!!canSubmit);
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          ))
        )}

        {/* Typing Indicator */}
        {showTypingIndicator && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, maxWidth: '85%' }}>
                <Box
                  component="img"
                  src={tess}
                  alt="Tess"
                  sx={{ 
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                  }}
                />
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'white',
                    border: '1px solid black',
                    borderRadius: 2,
                    borderTopLeftRadius: 0,
                    fontSize: '0.9375rem',
                    lineHeight: 1.5,
                    color: '#1A1A1A',
                    position: 'relative',
                    minHeight: '52px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: -8,
                      width: 8,
                      height: 8,
                      borderTop: '1px solid black',
                      borderLeft: '1px solid black',
                      backgroundColor: 'white',
                      transform: 'translateY(8px) rotate(-45deg)',
                    }
                  }}
                >
                  {/* Animated typing dots */}
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#22537f',
                          animation: 'typingDot 1.4s infinite ease-in-out',
                          animationDelay: `${i * 0.16}s`,
                          '@keyframes typingDot': {
                            '0%, 80%, 100%': {
                              transform: 'scale(0.8)',
                              opacity: 0.5,
                            },
                            '40%': {
                              transform: 'scale(1)',
                              opacity: 1,
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>
                  <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic', ml: 1 }}>
                    {typingMessage}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
        </Box>

        {/* All Cases Panel - Shows all 5 cases with relevancy status */}
        {allCases.length > 0 && (
          <AllCasesPanel cases={allCases} />
        )}

        {/* Search Results Panel - Shows only relevant cases */}
        {currentSearchResults.length > 0 && (
          <SearchResultsPanel results={currentSearchResults} />
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mx: 3, mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}



        {/* Session Ended Banner */}
        {sessionEnded && (
          <Box sx={{
            p: 2,
            bgcolor: '#FEF3C7',
            borderTop: '1px solid #F59E0B',
            borderBottom: '1px solid #F59E0B',
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ color: '#92400E', fontWeight: 'medium' }}>
              🐾 Session ended. Start a new session to ask about another pet.
            </Typography>
          </Box>
        )}

        {/* Message Input - Hidden when form is shown */}
        {!showBaseQuestionsForm && (
          <Box sx={{ 
            p: 2,
            borderTop: '1px solid #E5E7EB',
            bgcolor: hasActiveOptions ? '#f8f9fa' : 'white',
            transition: 'background-color 0.2s ease',
          }}>
            <Box sx={{ 
              display: 'flex', 
              gap: 1.5, 
              alignItems: 'center',
              bgcolor: hasActiveOptions ? '#e9ecef' : '#F4F6F8',
              borderRadius: 3,
              p: 1,
              opacity: hasActiveOptions ? 0.7 : 1,
              transition: 'all 0.2s ease',
            }}>
              <TextField
                fullWidth
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  sessionEnded ? "Session ended - Please start a new session" :
                  hasActiveOptions ? "Select options above and click Send" :
                  "Describe your pet's symptoms..."
                }
                disabled={showTypingIndicator || sessionEnded}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  readOnly: hasActiveOptions,
                }}
                sx={{
                  ml: 1,
                  '& .MuiInputBase-input': {
                    fontSize: '0.9375rem',
                  },
                }}
              />
              {/* Show button or icon based on state */}
              {(hasActiveOptions && canSubmitOptions) || (!hasActiveOptions && message.trim()) ? (
                <Button
                  onClick={sendMessage}
                  disabled={showTypingIndicator || sessionEnded}
                  variant="contained"
                  endIcon={showTypingIndicator ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                  sx={{
                    bgcolor: '#1a365d', // Dark blue
                    color: 'white',
                    minWidth: '100px',
                    height: '44px',
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    px: 2,
                    '&:hover': {
                      bgcolor: '#153454',
                    },
                    '&:disabled': {
                      bgcolor: '#94a3b8',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {showTypingIndicator ? 'Sending...' : 'Submit'}
                </Button>
              ) : (
                <IconButton
                  onClick={sendMessage}
                  disabled={true}
                  sx={{ 
                    bgcolor: '#94a3b8',
                    color: 'white',
                    '&:disabled': {
                      bgcolor: '#94a3b8',
                    }
                  }}
                >
                  <SendIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        )}
          </>
        ) : (
          // Reviews Tab Content
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {currentSession ? (
              <ReviewComponent 
                sessionId={currentSession}
                onReviewAdded={() => {
                  // Refresh reviews if needed
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Select a chat session to view or add improvement notes
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Sessions Drawer */}
      <Drawer
        anchor="left"
        open={showSessions}
        onClose={() => setShowSessions(false)}
      >
        <Box sx={{ width: 350, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Chat Sessions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {sessions.map((session) => (
              <ListItem key={session.session_id} disablePadding>
                <ListItemButton
                  onClick={async () => {
                    try {
                      await loadSessionHistory(session.session_id);
                      setShowSessions(false);
                    } catch (err) {
                      console.error('Error selecting session:', err);
                      setError('Failed to load selected chat session');
                    }
                  }}
                  selected={session.session_id === currentSession}
                >
                  <ListItemText
                    primary={session.title || 'Untitled Session'}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          {session.message_count} messages
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(session.created_at)}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      onClick={(e) => handleShareClick(e, session.session_id)}
                      size="small"
                      title="Share conversation"
                    >
                      <ShareIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSession(session.session_id);
                      }}
                      size="small"
                      title="Delete conversation"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Share Menu */}
      <Menu
        anchorEl={shareMenuAnchor}
        open={Boolean(shareMenuAnchor)}
        onClose={handleShareMenuClose}
      >
        <MenuItem onClick={shareSession}>
          <ContentCopyIcon sx={{ mr: 1 }} />
          Copy Share Link
        </MenuItem>
      </Menu>

      {/* Share Snackbar */}
      <Snackbar
        open={showShareSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowShareSnackbar(false)}
        message={shareSnackbarMessage}
      />
    </Box>
  );
};

export default ChatInterface; 